import { css } from '@emotion/react'
import { Button, Cell, Grid, HFlow, Theme } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import { ContextualHelpCard } from 'components/ContextualHelpCard'
import { RichTextField, SimNaoEnum, SimNaoRadioGroupField } from 'components/form'
import theme from 'config/theme'
import { parseISO } from 'date-fns'
import {
  FaixaEtariaConsumoAlimentar,
  TipoAtendimentoProfissional,
  TipoEstabelecimentoEnum,
} from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import preNatalContextualHelpImage from 'images/atendimento/acompanhamentos/pre-natal/pre-natal-contextual-help.webp'
import puerperioContextualHelpImage from 'images/atendimento/acompanhamentos/puerperio/puerperio-contextual-help.webp'
import React, { useMemo } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { MetaPath } from 'util/metaPath'
import {
  AntropometriaAccordion,
  AntropometriaAccordionModel,
} from 'view/atendimentos/components/MedicoesPanel/AntropometriaAccordion'

import {
  SinaisVitaisAccordion,
  SinaisVitaisAccordionModel,
} from '../../../components/MedicoesPanel/SinaisVitaisAccordion'
import {
  ACOMPANHAMENTOS_PRE_NATAL_URL,
  ACOMPANHAMENTOS_PUERPERIO_URL,
} from '../../acompanhamentos/getAcompanhamentosSideMenuItems'
import { INFO_GESTACAO_URL } from '../../acompanhamentos/pre-natal/AcompanhamentoPreNatalTabs'
import { useAcompanhamentoPuerperio } from '../../acompanhamentos/puerperio/hooks/useAcompanhamentoPuerperio'
import { PeriodoGestacaoModel } from '../../components/modals/types/PeriodoGestacaoModel'
import { AgendarProximasConsultasSectionModel } from '../finalizacao/components/agendar-consulta/model-agendarConsultaAtendimento'
import { IgDumPaper } from '../pre-natal/components/IgDumPaper'
import { PreNatalFormModel, TipoPreNatal } from '../pre-natal/model-preNatal'
import { grupoCboAnexoArquivos, grupoCboMedicoes, grupoCboResultadosExames } from './acessos'
import { AnexoArquivosView } from './anexo-arquivos/AnexoArquivosView'
import { AnexoArquivoFormModel } from './anexo-arquivos/model-anexoArquivos'
import { DumObjetivoForm } from './components/DumObjetivoForm'
import { NecessidadesEspeciaisField } from './components/NecessidadesEspeciaisField'
import { SwitchButtonObjetivoForm, SwitchButtonObjetivoFormProps } from './components/SwitchButtonObjetivoForm'
import { MarcadoresConsumoAlimentarPanel } from './marcadores-consumo-alimentar/MarcadoresConsumoAlimentarPanel'
import { MarcadoresConsumoPanelModel } from './marcadores-consumo-alimentar/model'
import { calculateFaixaEtariaMarcadoresConsumoAlimentar } from './marcadores-consumo-alimentar/utils'
import { SwitchButtonObjetivoFormModel } from './model'
import { ResultadosExamesModel } from './resultados-exames/model-resultadosExames'
import { ResultadosExamesRootView } from './resultados-exames/ResultadosExamesRootView'

export interface ObjetivoFormModel {
  texto?: string
  dum?: string
  vacinacaoEmDia?: SimNaoEnum
  sinaisVitais?: SinaisVitaisAccordionModel
  antropometria?: AntropometriaAccordionModel
  marcadoresConsumoAlimentar?: MarcadoresConsumoPanelModel
  necessidadesEspeciais?: boolean
  resultadosExames?: ResultadosExamesModel
  atendimentosEspecificos?: SwitchButtonObjetivoFormModel
  anexoArquivos?: ReadonlyArray<AnexoArquivoFormModel>
}

export interface ObjetivoFormProps extends Omit<SwitchButtonObjetivoFormProps, 'name' | 'metasPreNatal'> {
  name: MetaPath<ObjetivoFormModel>
  atendimentoProfissionalId: ID
  isAtendimentoOdonto: boolean
  isAtendimentoProcedimentos: boolean
  metaPreNatal: MetaPath<PreNatalFormModel>
  metaAgendarProximasConsultas: MetaPath<AgendarProximasConsultasSectionModel>
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  hasProblemaComCiapW78AtivoPersistido: boolean
}

export const ObjetivoForm = React.memo((props: Readonly<ObjetivoFormProps>) => {
  const {
    name,
    cidadao,
    dataAtendimento,
    prontuarioId,
    atendimentoProfissionalId,
    metaPreNatal,
    metaAgendarProximasConsultas,
    metaProcedimentosPlano,
    tipoPreNatal,
    metaProblemasCondicoes,
    gestacoes,
    isAtendimentoOdonto,
    isAtendimentoProcedimentos,
    hasProblemaComCiapW78AtivoPersistido,
  } = props

  const match = useRouteMatch()
  const history = useHistory()

  const {
    cidadao: { isGestante, dataNascimento },
    permissoes: { hasPermissionPreNatal },
    atendimentoProfissional: { iniciadoEm, tipo: tipoAtendimentoProfissional },
    tipoEstabelecimento,
    observacao,
  } = useAtendimentoContext()

  const showDum = useMemo(() => {
    return (
      !isAtendimentoProcedimentos &&
      cidadao.informarDum &&
      (!isGestante || tipoPreNatal === TipoPreNatal.PRIMEIRO_ATENDIMENTO_PRE_NATAL)
    )
  }, [cidadao.informarDum, isAtendimentoProcedimentos, isGestante, tipoPreNatal])

  const isPrimeiroAtendimentoPreNatal = useMemo(() => {
    return tipoPreNatal === TipoPreNatal.PRIMEIRO_ATENDIMENTO_PRE_NATAL
  }, [tipoPreNatal])

  const shouldShowMCA =
    tipoAtendimentoProfissional !== TipoAtendimentoProfissional.VACINACAO &&
    !(isAtendimentoOdonto && tipoEstabelecimento === TipoEstabelecimentoEnum.CEO)

  const showPreNatal = isGestante && hasPermissionPreNatal && !isAtendimentoProcedimentos

  const { data: informacoesPuerperio } = useAcompanhamentoPuerperio(prontuarioId, iniciadoEm.getTime())

  const showAcompPuerperioContextualHelp = informacoesPuerperio?.acompanhamentoAtivo && !isAtendimentoProcedimentos

  const categoriaMarcadoresConsumoAlimentar: FaixaEtariaConsumoAlimentar = useMemo(
    () =>
      shouldShowMCA && calculateFaixaEtariaMarcadoresConsumoAlimentar(parseISO(dataNascimento), new Date(iniciadoEm)),
    [dataNascimento, iniciadoEm, shouldShowMCA]
  )

  const classes = createStyles(theme)

  const redirectToAcompanhamentoPreNatal = () => {
    const path = match.url.replace('/soap', `/${ACOMPANHAMENTOS_PRE_NATAL_URL}/${INFO_GESTACAO_URL}?redirect=prenatal`)
    history.push(path)
  }

  const redirectToAcompanhamentoPuerperio = () => {
    const path = match.url.replace('/soap', `/${ACOMPANHAMENTOS_PUERPERIO_URL}`)
    history.push(path)
  }

  return (
    <Grid>
      <Cell size={12}>
        <RichTextField
          name={name.texto}
          maxLength={4000}
          placeholder='Insira as demais informações relacionadas ao exame físico.'
          dataTestId='observacoesObjetivo'
        />
      </Cell>

      <CheckPermission permission={grupoCboAnexoArquivos}>
        <Cell size={12}>
          <AnexoArquivosView name={name.anexoArquivos} />
        </Cell>
      </CheckPermission>

      <Cell size={12} alignSelf='flex-start'>
        <SwitchButtonObjetivoForm
          name={name.atendimentosEspecificos}
          metasPreNatal={{
            metaDum: name.dum,
            metaPreNatal,
            metaAgendarProximasConsultas,
          }}
          metaProcedimentosPlano={metaProcedimentosPlano}
          cidadao={cidadao}
          prontuarioId={prontuarioId}
          dataAtendimento={dataAtendimento}
          tipoPreNatal={tipoPreNatal}
          metaProblemasCondicoes={metaProblemasCondicoes}
          hasProblemaComCiapW78AtivoPersistido={hasProblemaComCiapW78AtivoPersistido}
        />
      </Cell>
      {showPreNatal && (
        <Cell size={12}>
          <ContextualHelpCard
            title='Novo módulo de acompanhamento de pré-natal. Todas as informações agora num só lugar!'
            description='Visualize consultas, exames, medicamentos, visitas, medições e outros dados do pré-natal. '
            image={preNatalContextualHelpImage}
            imageHeight={80}
            showVerMais={false}
          >
            <Button skin='outline' kind='primary' size='small' onClick={redirectToAcompanhamentoPreNatal}>
              Ver acompanhamento
            </Button>
          </ContextualHelpCard>
        </Cell>
      )}

      {showAcompPuerperioContextualHelp && (
        <Cell size={12}>
          <ContextualHelpCard
            title='Novo módulo de acompanhamento de puerpério. Todas as informações agora num só lugar! '
            description='Visualize consultas, exames, medicamentos, visitas, medições e outros dados do puerpério. '
            image={puerperioContextualHelpImage}
            imageHeight={80}
            showVerMais={false}
          >
            <Button skin='outline' kind='primary' size='small' onClick={redirectToAcompanhamentoPuerperio}>
              Ver acompanhamento
            </Button>
          </ContextualHelpCard>
        </Cell>
      )}

      {showDum && (
        <Cell size={8}>
          <HFlow>
            <DumObjetivoForm name={name.dum} tipoPreNatal={tipoPreNatal} />
            {isPrimeiroAtendimentoPreNatal && <IgDumPaper name={name.dum} />}
          </HFlow>
        </Cell>
      )}
      <CheckPermission permission={grupoCboMedicoes.adicionar}>
        <Cell size={12}>
          <SinaisVitaisAccordion name={name.sinaisVitais} style={classes.medicoesPanelStyle} />
        </Cell>
        <Cell size={12}>
          <AntropometriaAccordion
            name={name.antropometria}
            dataAtendimento={iniciadoEm}
            cidadao={cidadao}
            gestacoes={gestacoes}
            isGestante={isGestante}
            style={classes.medicoesPanelStyle}
            prontuarioId={prontuarioId}
            atendimentoProfissionalId={atendimentoProfissionalId}
            tipoPreNatal={tipoPreNatal}
            isAtendimentoObservacao={observacao.isAtendimentoObservacao}
            isObservacaoAndAuxiliar={observacao.isObservacaoAndAuxiliar}
            isAtendimentoProcedimento={isAtendimentoProcedimentos}
            isAtendimentoOdonto={isAtendimentoOdonto}
          />
        </Cell>
      </CheckPermission>

      {shouldShowMCA && (
        <Cell size={12}>
          <MarcadoresConsumoAlimentarPanel
            name={name.marcadoresConsumoAlimentar}
            faixaEtariaMCA={categoriaMarcadoresConsumoAlimentar}
            atendimentoObservacao={observacao}
          />
        </Cell>
      )}

      <Cell xs={4} lg={3}>
        <SimNaoRadioGroupField
          name={name.vacinacaoEmDia}
          label='Vacinação em dia?'
          clearFieldLabel='Vacinação em dia'
          clearable={true}
        />
      </Cell>

      {isAtendimentoOdonto && (
        <Cell size={12}>
          <NecessidadesEspeciaisField name={name.necessidadesEspeciais} label='Paciente com necessidades especiais' />
        </Cell>
      )}
      {!isAtendimentoProcedimentos && (
        <CheckPermission permission={grupoCboResultadosExames.adicionar}>
          <Cell size={12}>
            <ResultadosExamesRootView
              name={name.resultadosExames}
              prontuarioId={prontuarioId}
              cidadaoSexo={cidadao.sexo}
              cidadaoDataNascimento={cidadao.dataNascimento}
              cidadaoIdentidadeGeneroDbEnum={cidadao.identidadeGeneroDbEnum}
            />
          </Cell>
        </CheckPermission>
      )}
    </Grid>
  )
})

const createStyles = (theme: Theme) => ({
  medicoesPanelStyle: css`
    margin: 0;
    border-radius: 2px;
    border: 1px solid ${theme.pallete.gray.c40};
    box-shadow: ${theme.shadows.outer[10]};
  `,
})
